import React, { useState, useEffect, useContext } from "react";
import { Tabs, Tab } from "react-bootstrap";
import SuperAppObject from "./superappobject/SuperAppObject";
import { useMyContext } from "../MyContext";
import { fetchSecure } from "../utils/utils";
import {
  getSuperAppConfigSync,
  loadSuperAppConfig,
  restoreFunctions,
} from "../lib/superappConfig";
import GlobalContext from "../GlobalContext";
import VisualPageRenderer from "./VisualPageRenderer";

/**
 * LOW CODE BLOCK RENDERER
 *
 * Renders various block types defined in SuperApp config layouts:
 * - text, markdown, hero, cards, cta: Static content blocks
 * - superappobject: Dynamic SuperAppObject component loaded from config collection
 *
 * For superappobject blocks:
 * - Fetches configObj from database (config collection)
 * - Extracts the specific configObj (e.g., "Users") from app_config
 * - Renders SuperAppObject with the fetched config
 */

export default function LowCodeBlock({
  definition,
  user: propUser,
  userDeep: propUserDeep,
}) {
  /* Vik (Jan 21, 2026) : Use props if available, otherwise fallback to context to avoid prop drilling */
  const { userId, userData } = useMyContext();
  const user = propUser || userId;
  const userDeep = propUserDeep || userData;
  if (!definition) return null;
  const { type } = definition;

  switch (type) {
    case "text":
      return (
        <div className="lowcode-block text-block" style={{ color: "#fff" }}>
          {definition.title && (
            <h2 style={{ color: "#fff" }}>{definition.title}</h2>
          )}
          <p style={{ color: "#fff" }}>{definition.value || ""}</p>
        </div>
      );
    case "markdown":
      return (
        <div className="lowcode-block markdown-block" style={{ color: "#fff" }}>
          <div
            dangerouslySetInnerHTML={{
              __html: (definition.text || "").replace(/\n/g, "<br/>"),
            }}
            style={{ color: "#fff" }}
          />
        </div>
      );
    case "html":
      // Check if it's a Visual Builder JSON string
      let isVisualBuilderContent = false;
      try {
        const parsed = JSON.parse(definition.html || "");
        if (parsed && parsed.elements && Array.isArray(parsed.elements)) {
          isVisualBuilderContent = true;
        }
      } catch (e) {
        // Not JSON, treat as standard HTML
      }

      if (isVisualBuilderContent) {
        return (
          <div className="lowcode-block html-block" style={{ color: "#fff" }}>
            <VisualPageRenderer content={definition.html} />
          </div>
        );
      }

      // Decode HTML entities (ReactQuill escapes HTML code, so we need to decode it)
      const decodeHtml = (html) => {
        if (!html) return "";
        const textarea = document.createElement("textarea");
        textarea.innerHTML = html;
        return textarea.value;
      };
      const decodedHtml = decodeHtml(definition.html || "");
      return (
        <div className="lowcode-block html-block" style={{ color: "#fff" }}>
          {definition.title && (
            <h2 style={{ color: "#fff", marginBottom: 16 }}>
              {definition.title}
            </h2>
          )}
          <div
            dangerouslySetInnerHTML={{ __html: decodedHtml }}
            style={{ color: "#fff" }}
          />
        </div>
      );
    case "hero":
      return (
        <div className="lowcode-block hero-block" style={{ color: "#fff" }}>
          {definition.title && (
            <h1 style={{ color: "#fff" }}>{definition.title}</h1>
          )}
          {definition.subtitle && (
            <p className="hero-subtitle" style={{ color: "#fff" }}>
              {definition.subtitle}
            </p>
          )}
          {definition.cta && definition.cta.href && (
            <a
              className="primary-gradient-btn"
              href={definition.cta.href}
              target={definition.cta.target || "_self"}
            >
              {definition.cta.label || "Learn more"}
            </a>
          )}
        </div>
      );
    case "cards":
      return (
        <div className="lowcode-block cards-block" style={{ color: "#fff" }}>
          {definition.title && (
            <h2 style={{ color: "#fff" }}>{definition.title}</h2>
          )}
          <div className="cards-grid">
            {(definition.items || []).map((card, idx) => (
              <div key={idx} className="card-item" style={{ color: "#fff" }}>
                {card.title && <h3 style={{ color: "#fff" }}>{card.title}</h3>}
                {card.text && <p style={{ color: "#fff" }}>{card.text}</p>}
                {card.link && card.link.href && (
                  <a
                    href={card.link.href}
                    target={card.link.target || "_self"}
                    rel="noreferrer"
                    style={{ color: "#fff" }}
                  >
                    {card.link.label || "Open"}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    case "cta":
      return (
        <div
          className="lowcode-block cta-block"
          style={{ textAlign: "center", color: "#fff" }}
        >
          {definition.title && (
            <h2 style={{ color: "#fff" }}>{definition.title}</h2>
          )}
          {definition.text && (
            <p style={{ color: "#fff" }}>{definition.text}</p>
          )}
          {definition.button && definition.button.href && (
            <a
              className="primary-gradient-btn"
              href={definition.button.href}
              target={definition.button.target || "_self"}
            >
              {definition.button.label || "Continue"}
            </a>
          )}
        </div>
      );
    case "superappobject":
      // Render a SuperAppObject loaded from config collection
      return (
        <SuperAppObjectBlock
          definition={definition}
          user={user}
          userDeep={userDeep}
        />
      );
    case "tabs":
      return (
        <div className="lowcode-block tabs-block" style={{ color: "#fff" }}>
          <Tabs
            defaultActiveKey={
              definition.tabs && definition.tabs.length > 0
                ? definition.tabs[0].id
                : null
            }
            id={definition.id || "tabs-block"}
            className="mb-3 custom-tabs"
          >
            {(definition.tabs || []).map((tab, idx) => (
              <Tab eventKey={tab.id} title={tab.label} key={tab.id}>
                <div style={{ padding: "20px 0" }}>
                  {/* Recursively render the content of the tab */}
                  {tab.block &&
                  definition.blocks &&
                  definition.blocks[tab.block] ? (
                    <LowCodeBlock
                      definition={definition.blocks[tab.block]}
                      user={user}
                      userDeep={userDeep}
                    />
                  ) : (
                    <div
                      style={{
                        padding: 20,
                        textAlign: "center",
                        color: "#aaa",
                        border: "1px dashed rgba(255,255,255,0.2)",
                        borderRadius: 8,
                      }}
                    >
                      No content configured for this tab
                    </div>
                  )}
                </div>
              </Tab>
            ))}
          </Tabs>
        </div>
      );
    default:
      return (
        <pre className="lowcode-block json-block">
          {JSON.stringify(definition, null, 2)}
        </pre>
      );
  }
}

/**
 * SUPERAPP OBJECT BLOCK
 *
 * Fetches a configObj from the database (config collection) and renders
 * it as a SuperAppObject component.
 *
 * Expected definition structure:
 * {
 *   type: "superappobject",
 *   configObj: "Users",  // Name of the configObj in app_config (required)
 *   collection_name: "users",  // Optional: override collection name
 *   displayState: "TABLE",  // Optional: display state (default: "TABLE")
 *   title: "Users"  // Optional: title for the SuperAppObject
 * }
 */
function SuperAppObjectBlock({ definition, user, userDeep }) {
  const {
    configObj,
    collection_name,
    displayState,
    displayType,
    title,
    submit,
  } = definition;
  // displayType takes precedence over displayState for consistency with boss's terminology
  const finalDisplayState = displayType || displayState || "TABLE";
  const myContext = useMyContext();
  const superAppStyle = useContext(GlobalContext)["superAppStyle"];
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitFunction, setSubmitFunction] = useState(null);

  // Helper to restore function from stringified format
  const restoreFunction = (func) => {
    if (!func) return null;
    if (typeof func === "function") return func;
    // Use _functionString instead of __function__ (Firestore reserves __ prefix)
    if (typeof func === "object" && func._functionString) {
      try {
        return eval(`(${func._functionString})`);
      } catch (e) {
        console.warn("Failed to restore submit function:", e);
        return null;
      }
    }
    if (typeof func === "string") {
      try {
        return eval(`(${func})`);
      } catch (e) {
        console.warn("Failed to restore submit function from string:", e);
        return null;
      }
    }
    return null;
  };

  useEffect(() => {
    // Restore submit function if provided
    if (submit) {
      const restored = restoreFunction(submit);
      setSubmitFunction(restored);
    } else {
      setSubmitFunction(null);
    }
  }, [submit]);

  useEffect(() => {
    async function loadConfigObj() {
      if (!configObj) {
        setError("configObj name is required for superappobject blocks");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // First, try to get from SuperApp config (stored in superapp_configs)
        const appId =
          new URLSearchParams(window.location.search).get("appId") ||
          process.env.REACT_APP_SUPERAPP_ID ||
          "Ben-test";

        // Try sync first (from cache)
        let superAppConfigRaw = getSuperAppConfigSync(appId);

        // If not in cache, try loading it
        if (!superAppConfigRaw) {
          try {
            const loaded = await loadSuperAppConfig(appId);
            superAppConfigRaw = loaded?.config || loaded;
          } catch (e) {
            console.warn("Failed to load SuperApp config:", e);
          }
        }

        // Handle both wrapped { config: {...} } and unwrapped {...} structures
        const superAppConfig = superAppConfigRaw?.config || superAppConfigRaw;
        if (
          superAppConfig &&
          superAppConfig.app_config &&
          superAppConfig.app_config[configObj]
        ) {
          console.log(`✅ Found ${configObj} in SuperApp config's app_config`);
          const foundConfig = superAppConfig.app_config[configObj];

          // DEBUG: Inspect loaded config for functions
          console.log(
            `[LowCodeBlock] Loaded config for ${configObj}:`,
            foundConfig,
          );
          if (foundConfig.table && foundConfig.table.length > 0) {
            const firstCol = foundConfig.table[0];
            console.log(
              `[LowCodeBlock] First column selector type: ${typeof firstCol.selector}`,
            );
            if (typeof firstCol.selector !== "function") {
              console.warn(
                `[LowCodeBlock] ⚠️ Selector is NOT a function! Value:`,
                firstCol.selector,
              );
              if (firstCol._functionString) {
                console.warn(
                  `[LowCodeBlock] Found _functionString but it wasn't restored:`,
                  firstCol._functionString,
                );
              }
            }
          }

          setConfig(foundConfig);
          setLoading(false);
          return;
        }

        // Second, try to use configData from MyContext (from config collection - legacy)
        const contextConfigRaw = myContext?.configData;
        const contextConfig = contextConfigRaw?.config || contextConfigRaw;
        if (
          contextConfig &&
          contextConfig.app_config &&
          contextConfig.app_config[configObj]
        ) {
          console.log(
            `✅ Found ${configObj} in MyContext configData's app_config`,
          );
          setConfig(contextConfig.app_config[configObj]);
          setLoading(false);
          return;
        }

        console.warn(
          `⚠️ ConfigObj "${configObj}" not found in app_config. Available keys:`,
          superAppConfig?.app_config
            ? Object.keys(superAppConfig.app_config)
            : contextConfig?.app_config
              ? Object.keys(contextConfig.app_config)
              : "none",
        );

        // Third, try to fetch from superapp_objects collection
        try {
          const superAppObjectResponse = await fetchSecure(
            `/api/superapp/objects/${configObj}`,
          );
          if (superAppObjectResponse.ok) {
            const superAppObject = await superAppObjectResponse.json();
            if (superAppObject && superAppObject.config) {
              console.log(
                `✅ Found ${configObj} in superapp_objects collection`,
              );
              // Convert SuperAppObject format to configObj format
              // Ensure required properties exist with defaults
              const convertedConfig = restoreFunctions({
                ...superAppObject.config,
                name: superAppObject.displayName || configObj,
                tableName: superAppObject.config.tableName || configObj,
                collection: superAppObject.config.collection || configObj,
                // Ensure table (columns) exists - required by SuperAppObject
                table:
                  superAppObject.config.table ||
                  superAppObject.config.columns ||
                  [],
                // Ensure fields exists - required by SuperAppObject for forms
                fields: superAppObject.config.fields || [],
                // Ensure otherTableOptions exists - required for pagination
                otherTableOptions: superAppObject.config.otherTableOptions || {
                  paginationPerPage: 10,
                },
              });
              setConfig(convertedConfig);
              setLoading(false);
              return;
            }
          } else if (superAppObjectResponse.status === 404) {
            console.log(
              `SuperAppObject "${configObj}" not found in superapp_objects collection, trying legacy config collection...`,
            );
          }
        } catch (e) {
          console.warn(
            `Failed to fetch ${configObj} from superapp_objects collection:`,
            e,
          );
        }

        // Fourth, if not found in SuperApp config or context, fetch from API (config collection - legacy)
        // Try fetchSecure first (if logged in), otherwise use regular fetch
        const token =
          localStorage.getItem("token") || localStorage.getItem("access_token");
        let response;
        try {
          if (token) {
            response = await fetchSecure("/api/getObjects/config");
          } else {
            // Try regular fetch if not logged in (some endpoints might work without auth)
            response = await fetch("/api/getObjects/config");
          }
        } catch (fetchError) {
          throw new Error(`Failed to fetch config: ${fetchError.message}`);
        }

        // Check if response is OK
        if (!response.ok) {
          const text = await response.text();
          throw new Error(
            `API error (${response.status}): ${text.substring(0, 100)}`,
          );
        }

        // Check content type to ensure we got JSON
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          // Clone response to read text without consuming the original
          const clonedResponse = response.clone();
          const text = await clonedResponse.text();
          throw new Error(
            `Expected JSON but got ${contentType}. Response: ${text.substring(
              0,
              200,
            )}`,
          );
        }

        const configArray = await response.json();

        if (!Array.isArray(configArray) || configArray.length === 0) {
          throw new Error(
            "No config found in database. Make sure the 'config' collection exists and has at least one document.",
          );
        }

        const fullConfig = configArray[0];
        if (!fullConfig.app_config || !fullConfig.app_config[configObj]) {
          const availableConfigs = fullConfig.app_config
            ? Object.keys(fullConfig.app_config).join(", ")
            : "none";
          throw new Error(
            `ConfigObj "${configObj}" not found in app_config. Available configs: ${availableConfigs}`,
          );
        }

        setConfig(fullConfig.app_config[configObj]);
      } catch (e) {
        console.error("Failed to load configObj:", e);
        setError(e.message || "Failed to load SuperAppObject config");
      } finally {
        setLoading(false);
      }
    }

    loadConfigObj();
  }, [configObj, myContext?.configData]);

  if (loading) {
    return (
      <div
        className="lowcode-block superappobject-block"
        style={{ padding: 24, color: "#fff" }}
      >
        <p>Loading {configObj}...</p>
      </div>
    );
  }

  if (error) {
    // Check if it's an HTML response error (API endpoint not found)
    const isHtmlError =
      error.includes("<!DOCTYPE") || error.includes("text/html");

    // For demo purposes, show a hardcoded table when API isn't available
    const showDemoTable =
      isHtmlError ||
      error.includes("not accessible") ||
      error.includes("authentication");

    if (showDemoTable) {
      // Hardcoded demo table with sample data
      const demoData = [
        {
          id: 1,
          name: "John Doe",
          email: "john@example.com",
          role: "Admin",
          status: "Active",
          created: "2024-01-15",
        },
        {
          id: 2,
          name: "Jane Smith",
          email: "jane@example.com",
          role: "User",
          status: "Active",
          created: "2024-02-20",
        },
        {
          id: 3,
          name: "Bob Johnson",
          email: "bob@example.com",
          role: "Editor",
          status: "Inactive",
          created: "2024-03-10",
        },
        {
          id: 4,
          name: "Alice Williams",
          email: "alice@example.com",
          role: "User",
          status: "Active",
          created: "2024-04-05",
        },
        {
          id: 5,
          name: "Charlie Brown",
          email: "charlie@example.com",
          role: "Admin",
          status: "Active",
          created: "2024-05-12",
        },
      ];

      return (
        <div
          className="lowcode-block superappobject-block"
          style={{ padding: 24, color: "#fff" }}
        >
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              background: "rgba(255,255,255,0.05)",
              borderRadius: 4,
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <p style={{ color: "#fff", margin: 0, fontSize: "0.9em" }}>
              <strong>📊 Demo Table:</strong> This is a sample table showing how
              the datagrid feature works. In production, this would be loaded
              from your database using the Table/Column Editor configuration.
            </p>
          </div>
          <div
            style={{
              overflowX: "auto",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 8,
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                color: "#fff",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    borderBottom: "2px solid rgba(255,255,255,0.2)",
                  }}
                >
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: "bold",
                      borderRight: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    ID
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: "bold",
                      borderRight: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: "bold",
                      borderRight: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    Email
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: "bold",
                      borderRight: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    Role
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: "bold",
                      borderRight: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: "bold",
                    }}
                  >
                    Created
                  </th>
                </tr>
              </thead>
              <tbody>
                {demoData.map((row, idx) => (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: "1px solid rgba(255,255,255,0.1)",
                      background:
                        idx % 2 === 0
                          ? "rgba(255,255,255,0.02)"
                          : "transparent",
                    }}
                  >
                    <td
                      style={{
                        padding: "12px 16px",
                        borderRight: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      {row.id}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        borderRight: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      {row.name}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        borderRight: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      {row.email}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        borderRight: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      {row.role}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        borderRight: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: 4,
                          background:
                            row.status === "Active"
                              ? "rgba(76, 175, 80, 0.3)"
                              : "rgba(244, 67, 54, 0.3)",
                          color:
                            row.status === "Active" ? "#4caf50" : "#f44336",
                          fontSize: "0.85em",
                        }}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px" }}>{row.created}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div
            style={{
              marginTop: 16,
              padding: 12,
              background: "rgba(255,255,255,0.05)",
              borderRadius: 4,
              fontSize: "0.85em",
              color: "#fff",
              opacity: 0.8,
            }}
          >
            <p style={{ margin: 0 }}>
              <strong>💡 Note:</strong> This is a demo table. To configure real
              tables, go to Menu Editor → Section 7: Table/Column Editor. All
              columns, sorting, and display options are configured in the
              database - no code changes needed!
            </p>
          </div>
        </div>
      );
    }

    // For other errors, show the error message
    return (
      <div
        className="lowcode-block superappobject-block"
        style={{
          padding: 24,
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.2)",
          borderRadius: 8,
          background: "rgba(255,255,255,0.05)",
        }}
      >
        <h3 style={{ color: "#fff", marginTop: 0 }}>
          Table Demo Setup Required
        </h3>
        <p style={{ color: "#fff", marginBottom: 16 }}>
          To see the table in action, you need to set up the config collection
          in your database.
        </p>
        <div
          style={{
            background: "rgba(255,107,107,0.1)",
            padding: 12,
            borderRadius: 4,
            marginBottom: 16,
          }}
        >
          <p style={{ color: "#ff6b6b", margin: 0, fontSize: "0.9em" }}>
            <strong>Error:</strong> {error}
          </p>
        </div>
        <div
          style={{
            background: "rgba(255,255,255,0.05)",
            padding: 16,
            borderRadius: 4,
          }}
        >
          <p style={{ color: "#fff", marginTop: 0, fontSize: "0.9em" }}>
            <strong>To set up the table demo:</strong>
          </p>
          <ol
            style={{
              color: "#fff",
              fontSize: "0.9em",
              paddingLeft: 20,
              marginBottom: 0,
            }}
          >
            <li>Go to the Menu Editor (section 7: Table/Column Editor)</li>
            <li>Select a configObj (e.g., "Users") or create a new one</li>
            <li>Configure the table columns using the visual editor</li>
            <li>The table will appear here once the config is set up</li>
          </ol>
        </div>
      </div>
    );
  }
  console.log("config ::", { user, userDeep, definition });
  // Vik (Jan 19, 2026) : Added return statement to ensure SuperAppObject is rendered when config is loaded.
  // Previously it was falling through and returning null.
  return (
    <div className="lowcode-block superappobject-block">
      <SuperAppObject
        key={configObj} // Force re-mount if configObj changes
        displayState={finalDisplayState}
        config={config}
        user={user}
        userDeep={userDeep}
        /* Vik (Jan 19, 2026) : Passed superAppStyle to ensure theme colors are applied and to prevent crashes in DisplayDetailsNew.js */
        style={superAppStyle}
        // Pass other props that might be needed
        collection_name={config.collection}
        id={definition.id || ""} // If editing a specific record
      />
    </div>
  );
}
